// GlobalStateContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import { ItemNotification, MenuItem } from '../../resources/types';

type GlobalState = {
  version: string; displayName: string; loading: boolean; JWT: string; sdk: string;
  returnUrl: string; RUT: string; idUser: number; phone: string; data: Record<string, any>;
  menu: MenuItem[]; notifications: ItemNotification[]; resources: Record<string, any>;
  parameters: Record<string, any>; counters: Record<string, any>;
};

const GlobalStateContext = createContext<GlobalState | undefined>(undefined);

// Bridge Object
export const globalStateStore: {
  setVersion?: (v: string) => void;
  setSdk?: (v: string) => void;
  setDisplayName?: (v: string) => void;
  setLoading?: (v: boolean) => void;
  setJWT?: (v: string) => void;
  setReturnUrl?: (v: string) => void;
  setRUT?: (v: string) => void;
  setIdUser?: (v: number) => void;
  setPhone?: (v: string) => void;
  setData?: (v: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)) => void;
  setMenu?: (v: MenuItem[] | ((prev: MenuItem[]) => MenuItem[])) => void;
  setNotifications?: (v: ItemNotification[] | ((prev: ItemNotification[]) => ItemNotification[])) => void;
  setResources?: (v: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)) => void;
  setParameters?: (v: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)) => void;
  setCounters?: (v: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)) => void;
} = {};

export const GlobalStateProvider = ({ children }: { children: React.ReactNode }) => {
  // --- Lazy Initialization (Load from LocalStorage ONCE on startup) ---
  const getStored = (key: string, fallback: any) => {
    const item = localStorage.getItem(key);
    if (!item) return fallback;
    try { return JSON.parse(item); } catch { return item; }
  };

  const [version, setVersionState] = useState(() => localStorage.getItem('version') || "0.0.0");
  const [sdk, setSdkState] = useState(() => localStorage.getItem('sdk') || "0.0.0");
  const [displayName, setDisplayNameState] = useState(() => localStorage.getItem('displayName') || "");
  const [loading, setLoadingState] = useState(false);
  const [JWT, setJWTState] = useState(() => localStorage.getItem('JWT') || "");
  const [returnUrl, setReturnUrlState] = useState(() => localStorage.getItem('returnUrl') || "");
  const [RUT, setRUTState] = useState(() => localStorage.getItem('RUT') || "");
  const [idUser, setIdUserState] = useState(() => Number(localStorage.getItem('idUser')) || 0);
  const [phone, setPhoneState] = useState(() => localStorage.getItem('phone') || "");
  const [data, setDataState] = useState<Record<string, any>>(() => getStored('data', {}));
  const [menu, setMenuState] = useState<MenuItem[]>(() => getStored('menu', []));
  const [notifications, setNotificationsState] = useState<ItemNotification[]>([]); // Not persistent per your request
  const [resources, setResourcesState] = useState<Record<string, any>>(() => getStored('resources', {}));
  const [parameters, setParametersState] = useState<Record<string, any>>(() => getStored('parameters', {}));
  const [counters, setCountersState] = useState<Record<string, any>>(() => getStored('counters', {}));

  // --- Registration ---
  useEffect(() => {
    globalStateStore.setVersion = setVersionState;
    globalStateStore.setSdk = setSdkState;
    globalStateStore.setDisplayName = setDisplayNameState;
    globalStateStore.setLoading = setLoadingState;
    globalStateStore.setJWT = setJWTState;
    globalStateStore.setReturnUrl = setReturnUrlState;
    globalStateStore.setRUT = setRUTState;
    globalStateStore.setIdUser = setIdUserState;
    globalStateStore.setPhone = setPhoneState;
    globalStateStore.setData = setDataState;
    globalStateStore.setMenu = setMenuState;
    globalStateStore.setNotifications = setNotificationsState;
    globalStateStore.setResources = setResourcesState;
    globalStateStore.setParameters = setParametersState;
    globalStateStore.setCounters = setCountersState;

    return () => { Object.keys(globalStateStore).forEach(k => (globalStateStore as any)[k] = undefined); };
  }, []);

  // --- Persistence Sync (The Correct Way) ---
  // This ensures that even functional updates are saved to localStorage
  useEffect(() => { localStorage.setItem('version', version); }, [version]);
  useEffect(() => { localStorage.setItem('sdk', sdk); }, [sdk]);
  useEffect(() => { localStorage.setItem('displayName', displayName); }, [displayName]);
  useEffect(() => { localStorage.setItem('JWT', JWT); }, [JWT]);
  useEffect(() => { localStorage.setItem('returnUrl', returnUrl); }, [returnUrl]);
  useEffect(() => { localStorage.setItem('RUT', RUT); }, [RUT]);
  useEffect(() => { localStorage.setItem('idUser', idUser.toString()); }, [idUser]);
  useEffect(() => { localStorage.setItem('phone', phone.toString()); }, [phone]);
  useEffect(() => { localStorage.setItem('data', JSON.stringify(data)); }, [data]);
  useEffect(() => { localStorage.setItem('menu', JSON.stringify(menu)); }, [menu]);
  useEffect(() => { localStorage.setItem('resources', JSON.stringify(resources)); }, [resources]);
  useEffect(() => { localStorage.setItem('parameters', JSON.stringify(parameters)); }, [parameters]);
  useEffect(() => { localStorage.setItem('counters', JSON.stringify(counters)); }, [counters]);

  return (
    <GlobalStateContext.Provider value={{
      version, sdk, displayName, loading, JWT, returnUrl, RUT, idUser, phone, data, menu, notifications, resources, parameters, counters
    }}>
      {children}
    </GlobalStateContext.Provider>
  );
};

export const useGlobalState = () => {
  const context = useContext(GlobalStateContext);
  if (!context) throw new Error("useGlobalState must be used within Provider");
  return context;
};